import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useConfig } from '@context/ConfigContext';
import BeforeAfterSlideshow from '@components/shared/BeforeAfterSlideshow/BeforeAfterSlideshow';
import Modal from '@components/shared/Modal/Modal';
import Button from '@components/shared/Button/Button';
import styles from './About.module.css';

const About: React.FC = () => {
  const { config } = useConfig();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  if (!config || !config.components.about.enabled) return null;

  const { about } = config.components;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const handleSlideshowClick = () => {
    if (about.beforeAfterSlideshow?.enabled && about.beforeAfterSlideshow.slides.length > 0) {
      setIsModalOpen(true);
      setCurrentSlideIndex(0);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentSlideIndex(0);
  };

  const handleNextSlide = () => {
    if (about.beforeAfterSlideshow) {
      const totalSlides = about.beforeAfterSlideshow.slides.length;
      setCurrentSlideIndex((prev) => (prev + 1) % totalSlides);
    }
  };

  const handlePrevSlide = () => {
    if (about.beforeAfterSlideshow) {
      const totalSlides = about.beforeAfterSlideshow.slides.length;
      setCurrentSlideIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
    }
  };

  const handleThumbnailClick = (index: number) => {
    setCurrentSlideIndex(index);
  };

  return (
    <section id="about" className={styles.about}>
      <div className={styles.container}>
        <motion.div
          className={styles.content}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {/* Section Header */}
          <motion.div className={styles.header} variants={itemVariants}>
            <h2>About Us</h2>
            <div className={styles.divider}></div>
          </motion.div>

          {/* Main Content - Two Column Layout */}
          <div className={styles.mainContent}>
            {/* Text Content (Left) */}
            <motion.div className={styles.textContent} variants={itemVariants}>
              <div
                dangerouslySetInnerHTML={{ __html: about.text }}
              />
            </motion.div>

            {/* BeforeAfterSlideshow (Right) - Clickable */}
            <motion.div 
              className={styles.imageContent} 
              variants={itemVariants}
              onClick={handleSlideshowClick}
              style={{ cursor: 'pointer' }}
            >
              {about.beforeAfterSlideshow && (
                <BeforeAfterSlideshow
                  slides={about.beforeAfterSlideshow.slides}
                  autoplay={about.beforeAfterSlideshow.autoplay}
                  interval={about.beforeAfterSlideshow.interval}
                  dissolveSpeed={about.beforeAfterSlideshow.dissolveSpeed}
                />
              )}
            </motion.div>
          </div>

          {/* Certifications */}
          <motion.div className={styles.certifications} variants={itemVariants}>
            <div className={styles.certificationsList}>
              {about.certifications.map((cert, index) => (
                <div key={index} className={styles.certificationItem}>
                  <i className={`${cert.icon} ${styles.certificationIcon}`} aria-hidden="true"></i>
                  <span className={styles.certificationName}>{cert.name}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Before/After Gallery Modal */}
      {isModalOpen && about.beforeAfterSlideshow && (
        <Modal
          isOpen={true}
          onClose={closeModal}
          title="Before & After Gallery"
          size="lg"
        >
          <div className={styles.modalContent}>
            {/* Manual Slideshow */}
            <div className={styles.manualSlideshow}>
              {/* Main Image Display */}
              <div className={styles.mainImageContainer}>
                {/* Before Image */}
                <div className={styles.beforeAfterContainer}>
                  <div className={styles.imageWrapper}>
                    <img
                      src={about.beforeAfterSlideshow.slides[currentSlideIndex].before}
                      alt={`Before - Slide ${currentSlideIndex + 1}`}
                      className={styles.mainImage}
                    />
                    <div className={styles.imageLabel}>Before</div>
                  </div>
                  <div className={styles.imageWrapper}>
                    <img
                      src={about.beforeAfterSlideshow.slides[currentSlideIndex].after}
                      alt={`After - Slide ${currentSlideIndex + 1}`}
                      className={styles.mainImage}
                    />
                    <div className={styles.imageLabel}>After</div>
                  </div>
                </div>
                
                {/* Navigation Arrows - Only show if more than 1 slide */}
                {about.beforeAfterSlideshow.slides.length > 1 && (
                  <>
                    <button
                      className={`${styles.navArrow} ${styles.navArrowLeft}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePrevSlide();
                      }}
                      aria-label="Previous slide"
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>
                    <button
                      className={`${styles.navArrow} ${styles.navArrowRight}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNextSlide();
                      }}
                      aria-label="Next slide"
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>

                    {/* Image Counter */}
                    <div className={styles.imageCounter}>
                      {currentSlideIndex + 1} / {about.beforeAfterSlideshow.slides.length}
                    </div>
                  </>
                )}
              </div>

              {/* Thumbnail Navigation - Only show if more than 1 slide */}
              {about.beforeAfterSlideshow.slides.length > 1 && (
                <div className={styles.thumbnailStrip}>
                  {about.beforeAfterSlideshow.slides.map((slide, slideIndex) => (
                    <div
                      key={slideIndex}
                      className={`${styles.thumbnail} ${slideIndex === currentSlideIndex ? styles.thumbnailActive : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleThumbnailClick(slideIndex);
                      }}
                    >
                      <img src={slide.after} alt={`Thumbnail ${slideIndex + 1}`} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.modalActions}>
              <Button variant="outline" size="lg" onClick={closeModal}>
                Close
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </section>
  );
};

export default About;